import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  createPatientWound,
  deletePatientWound,
  getPatientTasks,
  getPatientWounds,
  updatePatientWound,
} from '../../services/api';
import type {
  TaskListResponse,
  TaskStatus,
  WoundResponse,
} from '../../services/api/types.gen';
import WoundsCard from './WoundsCard';
import WoundFormDialog from './WoundFormDialog';
import WoundAssessmentsDrawer from './WoundAssessmentsDrawer';
import DeleteWoundDialog from './DeleteWoundDialog';
import { useStore } from '../../store';
import { toDateStr } from './woundUtils';
import { useWoundImages } from './useWoundImages';
import {
  contradictionAnchor,
  contradictionLoadingAnchor,
} from '../oasis/contradictions/contradictionNavigation';
import { EDITABLE_STATUSES } from '../../constants/statuses';
import { useClosedEpisodeLock } from '../../utils/closedEpisodeLock';
import { STATUS_DISPLAY_LABELS } from '../task/taskStatus';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

interface PatientWoundsProps {
  patientId: string;
  visitId?: string;
  visitStatus?: TaskStatus;
  /** True when the visit's period is closed. */
  episodeClosed?: boolean;
  /** True while the chart is an imported one still charted in the External
   *  EMR (Take Over v2): wounds read, never change. */
  chartLocked?: boolean;
  /**
   * The patient's visits, used by the assessments drawer to name the visit
   * each assessment was recorded on and to decide whether that visit still
   * accepts edits. Leave it off and the drawer fetches the list itself the
   * first time it opens; pass it when the parent already holds the list.
   */
  visits?: TaskListResponse[];
  onDocumentCreated?: () => void;
  /**
   * Called after the wound set changes (add/edit, discontinue, or delete). The
   * parent uses this to refresh its cached wound count so the tab badge stays
   * correct without waiting for a tab switch. Opening/closing the
   * assessments drawer is excluded — assessments move `assessment_count`, not
   * the non-discontinued wound count the badge shows.
   *
   * Consumers rely on this firing after *every* successful wound mutation, not
   * only when the badge count changes: `OASISForm` uses it to re-run Live
   * Scrubber validation, whose server-side contradiction snapshot reads wound
   * rows from the database. Do not narrow it to count-only changes.
   */
  onWoundsChanged?: () => void;
  /**
   * Fired after assessment create/update/delete so the parent can refresh
   * per-visit activity labels on the Wounds picker without touching the tab
   * badge (assessments do not change the non-discontinued wound count).
   */
  onWoundActivityChanged?: () => void;
  /**
   * Set when no visit can serve as wound context, e.g. none of the patient's
   * visit types has a wound section. The wound list always renders — wounds are
   * patient-scoped clinical history — and this banner explains why add / assess
   * / discontinue stay closed.
   *
   * Adding, discontinuing, and assessing stay disabled because each is
   * attributed to the visit it happened on and there is no visit here to
   * attribute to. Correcting and removing a wound name no visit, so they stay
   * available: a wound recorded on a visit type that has since lost its wound
   * section would otherwise be stranded.
   */
  noVisitContextReason?: string;
  /**
   * Visit picker rendered in the card toolbar next to Export / Add Wound.
   * State lives in the parent so the episode page can share the selected visit
   * across tabs. Omit when no visit can serve as wound context.
   */
  visitSelector?: React.ReactNode;
  /**
   * Patient-chart Wounds tab only. Visit notes leave this off so the table
   * stays flat and Live Scrubber can still jump to discontinued rows.
   */
  splitByStatus?: boolean;
}

/** Patient-scoped wound list. A visit is only required for add / assess / discontinue. */
const PatientWounds: React.FC<PatientWoundsProps> = ({
  patientId,
  visitId,
  visitStatus,
  episodeClosed,
  chartLocked = false,
  visits,
  onDocumentCreated,
  onWoundsChanged,
  onWoundActivityChanged,
  noVisitContextReason,
  visitSelector,
  splitByStatus = false,
}) => {
  // A locked visit blocks every wound write recorded against it; so does a
  // closed period, unless the user holds the amend-closed key.
  const { locked: closedEpisodeLocked } = useClosedEpisodeLock(episodeClosed);
  const isVisitLocked =
    !!visitId &&
    (!visitStatus ||
      !EDITABLE_STATUSES.has(visitStatus) ||
      closedEpisodeLocked);
  // Adding a wound, discontinuing one, and assessing one are all attributed to a
  // visit, so they need one whose status still allows edits. With no visit there
  // is nothing to attribute to, so they stay closed.
  const canAdd = !!visitId && !isVisitLocked && !chartLocked;
  // Correcting or removing a wound carries no visit reference: WoundUpdate has no
  // visit field and a delete names none, which is why the backend leaves both
  // ungated. So these stay open with no visit context, where the wound list is
  // otherwise read-only, and a wound recorded on a visit type that has since lost
  // its wound section can still be fixed or removed.
  const canEdit = !isVisitLocked && !chartLocked;
  const showError = useStore(state => state.showError);
  const [wounds, setWounds] = useState<WoundResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form dialog
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingWound, setEditingWound] = useState<WoundResponse | null>(null);

  // Delete confirmation
  const [deletingWound, setDeletingWound] = useState<WoundResponse | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);

  // Assessments drawer
  const [assessmentsWound, setAssessmentsWound] =
    useState<WoundResponse | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  // Fetched once, on the first drawer open, only when the parent passes no
  // `visits`. Without a visit list every card falls back to its created-at
  // date (the import timestamp on an imported chart) and reads as locked.
  const [fetchedVisits, setFetchedVisits] = useState<TaskListResponse[] | null>(
    null
  );
  const [visitsLoading, setVisitsLoading] = useState(false);
  const visitsFetchInFlight = useRef(false);
  useEffect(() => {
    setFetchedVisits(null);
    visitsFetchInFlight.current = false;
  }, [patientId]);

  const fetchWounds = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const resp = await getPatientWounds({
        path: { patient_id: patientId },
        query: { include_discontinued: true },
      });
      setWounds((resp.data as WoundResponse[]) ?? []);
    } catch {
      setError('Failed to load wounds');
    } finally {
      setIsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchWounds();
  }, [fetchWounds]);

  const {
    imagesByWound,
    downloadOnlyCountByWound,
    refresh: refreshWoundImages,
  } = useWoundImages(
    patientId,
    wounds.map(w => w.id)
  );

  const handleSaveWound = async (data: {
    location: string;
    wound_type: string;
    onset_date: string | null;
    present_on_admission: boolean;
    body_map_point?: { x: number; y: number } | null;
    wound_stage?: string | null;
    deepest_stage?: string | null;
    discontinued_date?: string | null;
    discontinued_reason?: string | null;
    discontinued_comments?: string | null;
  }) => {
    try {
      if (editingWound) {
        const resp = await updatePatientWound({
          path: { patient_id: patientId, wound_id: editingWound.id },
          body: {
            ...data,
            ...(visitId && data.discontinued_date
              ? { discontinued_on_visit_id: visitId }
              : {}),
          },
        });
        // Client has no throwOnError; a failed update returns { error } with no
        // data. Bail before touching state or firing the count refresh.
        if (resp.error) {
          // A rejected stage correction comes back with a message the
          // clinician can act on; anything else gets the generic copy.
          showError(
            getBusinessErrorMessage(resp) ??
              'Failed to update wound. Please try again.'
          );
          return;
        }
        const updated = resp.data as WoundResponse;
        setWounds(prev =>
          prev.map(w =>
            w.id === updated.id
              ? { ...updated, assessment_count: w.assessment_count }
              : w
          )
        );
        if (assessmentsWound?.id === updated.id)
          setAssessmentsWound(prev =>
            prev
              ? { ...updated, assessment_count: prev.assessment_count }
              : prev
          );
      } else {
        const resp = await createPatientWound({
          path: { patient_id: patientId },
          body: { ...data, added_on_visit_id: visitId! },
        });
        if (resp.error) {
          showError('Failed to create wound. Please try again.');
          return;
        }
        const created = resp.data as WoundResponse;
        setWounds(prev => [created, ...prev]);
      }
      setIsFormOpen(false);
      setEditingWound(null);
      // Creating adds to the badge count; editing can set/clear a discontinue
      // date, which moves it too.
      onWoundsChanged?.();
    } catch (err) {
      console.error('Failed to save wound:', err);
      showError(
        editingWound
          ? 'Failed to update wound. Please try again.'
          : 'Failed to create wound. Please try again.'
      );
    }
  };

  // Offered in the Add Wound dialog so a recurring pressure injury is
  // reactivated, keeping its staging history, instead of charted as new.
  const discontinuedPressureInjuries = useMemo(
    () =>
      wounds.filter(
        w => w.wound_type === 'pressure_injury' && !!w.discontinued_date
      ),
    [wounds]
  );

  const handleReactivateWound = async (woundId: string) => {
    try {
      // Clearing the discontinuation is the whole reactivation, and the
      // server keeps the discontinuing visit. The wound came back on the
      // selected visit, so the server dates the reactivation from it.
      const resp = await updatePatientWound({
        path: { patient_id: patientId, wound_id: woundId },
        body: {
          discontinued_date: null,
          discontinued_reason: null,
          discontinued_comments: null,
          ...(visitId ? { reactivated_on_visit_id: visitId } : {}),
        },
      });
      // Client has no throwOnError; a failed update returns { error }. Leave
      // the dialog open and do not fire the count refresh.
      if (resp.error) {
        console.error('Failed to reactivate wound:', resp.error);
        showError(
          getBusinessErrorMessage(resp) ??
            'Failed to reactivate wound. Please try again.'
        );
        return;
      }
      const updated = resp.data as WoundResponse;
      setWounds(prev => prev.map(w => (w.id === updated.id ? updated : w)));
      setIsFormOpen(false);
      setEditingWound(null);
      // Reactivating returns the wound to the badge's non-discontinued count.
      onWoundsChanged?.();
    } catch (err) {
      console.error('Failed to reactivate wound:', err);
      showError('Failed to reactivate wound. Please try again.');
    }
  };

  const handleDiscontinueWound = async (
    woundId: string,
    date: Date,
    reason: string,
    comments: string
  ) => {
    try {
      const resp = await updatePatientWound({
        path: { patient_id: patientId, wound_id: woundId },
        body: {
          discontinued_date: toDateStr(date),
          discontinued_reason: reason || null,
          discontinued_comments: comments.trim() || null,
          ...(visitId ? { discontinued_on_visit_id: visitId } : {}),
        },
      });
      // Client has no throwOnError; a failed discontinue returns { error }. Throw
      // so the outer catch surfaces the toast and HealWoundPopover stays open, and
      // we never fire onWoundsChanged on a failure.
      if (resp.error) throw resp.error;
      const updated = resp.data as WoundResponse;
      setWounds(prev => prev.map(w => (w.id === updated.id ? updated : w)));
      // Discontinuing drops the wound from the badge's non-discontinued count.
      onWoundsChanged?.();
    } catch (err) {
      console.error('Failed to discontinue wound:', err);
      showError('Failed to discontinue wound. Please try again.');
      throw err;
    }
  };

  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleDeleteWound = async () => {
    if (!deletingWound) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const response = await deletePatientWound({
        path: { patient_id: patientId, wound_id: deletingWound.id },
      });
      // Client has no throwOnError; a failed delete returns { error }. Don't
      // optimistically remove the row or fire the count refresh on failure.
      if (response.error) {
        setDeleteError('Failed to delete wound. Please try again.');
        return;
      }
      const deletedId = deletingWound.id;
      setWounds(prev => prev.filter(w => w.id !== deletedId));
      setDeletingWound(null);
      onWoundsChanged?.();
    } catch (err) {
      console.error('Failed to delete wound:', err);
      setDeleteError('Failed to delete wound. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleOpenAssessments = (wound: WoundResponse) => {
    setAssessmentsWound(wound);
    setIsDrawerOpen(true);
    if (
      visits === undefined &&
      fetchedVisits === null &&
      !visitsFetchInFlight.current
    ) {
      visitsFetchInFlight.current = true;
      setVisitsLoading(true);
      void (async () => {
        try {
          const resp = await getPatientTasks({
            path: { patient_id: patientId },
          });
          // The client does not throw on HTTP errors — it returns { error }.
          if (resp.error || !resp.data) {
            console.error(
              'Failed to load visits for wound assessments',
              resp.error
            );
            showError('Unable to load the visits for these assessments.');
            return;
          }
          // Same list the chart tab passes: visits only, never orders or
          // misc tasks. A failed load leaves `fetchedVisits` null, so the
          // next open tries again.
          setFetchedVisits(resp.data.filter(t => t.task_type === 'visit'));
        } catch (err) {
          console.error('Failed to load visits for wound assessments', err);
          showError('Unable to load the visits for these assessments.');
        } finally {
          setVisitsLoading(false);
          visitsFetchInFlight.current = false;
        }
      })();
    }
  };

  return (
    <>
      <div
        {...contradictionAnchor('wound_list')}
        {...contradictionLoadingAnchor(isLoading)}
      >
        {noVisitContextReason && (
          <div className='mb-3 inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800'>
            <AlertTriangle className='h-3.5 w-3.5 flex-shrink-0 text-amber-600' />
            {noVisitContextReason}
          </div>
        )}
        {isVisitLocked && (
          <div className='mb-3 inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800'>
            <AlertTriangle className='h-3.5 w-3.5 flex-shrink-0 text-amber-600' />
            Editing wounds is unavailable because this visit is{' '}
            {visitStatus
              ? (STATUS_DISPLAY_LABELS[visitStatus] ?? visitStatus)
              : 'not in an editable state'}
            .
          </div>
        )}
        <WoundsCard
          patientId={patientId}
          wounds={wounds}
          woundImages={imagesByWound}
          downloadOnlyCounts={downloadOnlyCountByWound}
          isLoading={isLoading}
          error={error}
          canAdd={canAdd}
          canEdit={canEdit}
          visitId={visitId}
          visitSelector={visitSelector}
          splitByStatus={splitByStatus}
          onDocumentCreated={onDocumentCreated}
          onRetry={fetchWounds}
          onAddWound={() => {
            setEditingWound(null);
            setIsFormOpen(true);
          }}
          onEditWound={wound => {
            setEditingWound(wound);
            setIsFormOpen(true);
          }}
          onDeleteWound={setDeletingWound}
          onDiscontinueWound={handleDiscontinueWound}
          onOpenAssessments={handleOpenAssessments}
        />

        <WoundFormDialog
          open={isFormOpen}
          onOpenChange={open => {
            setIsFormOpen(open);
            if (!open) setEditingWound(null);
          }}
          editingWound={editingWound}
          canDiscontinue={canAdd}
          onSave={handleSaveWound}
          discontinuedPressureInjuries={
            editingWound ? undefined : discontinuedPressureInjuries
          }
          onReactivate={handleReactivateWound}
        />

        <WoundAssessmentsDrawer
          wound={assessmentsWound}
          open={isDrawerOpen}
          readOnly={!canAdd}
          visitId={visitId}
          visits={visits ?? fetchedVisits ?? []}
          visitsLoading={visitsLoading}
          onAssessmentsChanged={onWoundActivityChanged}
          onOpenChange={open => {
            setIsDrawerOpen(open);
            if (!open) {
              fetchWounds();
              // The drawer is where photos are uploaded and deleted, so the
              // row thumbnails for that wound are stale as soon as it closes.
              // Refresh only the open wound — not every wound on the chart.
              if (assessmentsWound) {
                refreshWoundImages(assessmentsWound.id);
              }
            }
          }}
        />

        <DeleteWoundDialog
          wound={deletingWound}
          isDeleting={isDeleting}
          error={deleteError}
          onConfirm={handleDeleteWound}
          onClose={() => {
            setDeletingWound(null);
            setDeleteError(null);
          }}
        />
      </div>
    </>
  );
};

export default PatientWounds;
